// ============================================================================
// STRING LIST EDITOR
//
// An editable list of short text lines, used for acceptance criteria and for
// the new `notes` field on stories, NFRs, features and ADRs.
//
// Every row is keyed by a stable generated id rather than its array index.
// StoryEditModal previously keyed acceptance criteria by index and carried a
// NOSONAR suppression to silence the warning; index keys make React reuse the
// wrong input when a row is removed from the middle, so the text jumps between
// fields. NFREditModal already used a keyed shape, and that is the one kept.
//
// The file is named NotesEditor because notes are why it was extracted, but it
// is generic on purpose — acceptance criteria and notes are the same widget
// with different labels, and keeping one implementation is the point.
// ============================================================================

import React from 'react';
import { Plus, X } from 'lucide-react';

export interface KeyedValue {
  id: string;
  value: string;
}

let keyCounter = 0;

/** Wraps plain strings for editing. The prefix keeps ids readable while debugging. */
export function toKeyed(values: string[] | undefined, prefix = 'item'): KeyedValue[] {
  return (values || []).map((value, i) => ({ id: `${prefix}-${i}-${(keyCounter++).toString(36)}`, value }));
}

/** Unwraps for saving. Trims, and drops rows the user left blank. */
export function fromKeyed(items: KeyedValue[]): string[] {
  return items.map(i => i.value.trim()).filter(Boolean);
}

/** A new empty row. */
export function newKeyedValue(prefix = 'item', value = ''): KeyedValue {
  return { id: `${prefix}-new-${(keyCounter++).toString(36)}`, value };
}

export interface StringListEditorProps {
  label: string;
  icon?: React.ReactNode;
  items: KeyedValue[];
  onChange: (next: KeyedValue[]) => void;
  /** Prefix for generated ids, purely for readability. */
  idPrefix?: string;
  placeholder?: string;
  addLabel?: string;
  /** Shown under the label when the list is empty, to explain what belongs here. */
  emptyHint?: string;
  /** Notes are often a sentence or two, so they get a textarea. */
  multiline?: boolean;
}

export function StringListEditor({
  label,
  icon,
  items,
  onChange,
  idPrefix = 'item',
  placeholder,
  addLabel = 'Add',
  emptyHint,
  multiline = false,
}: Readonly<StringListEditorProps>) {
  const update = (id: string, value: string) =>
    onChange(items.map(i => (i.id === id ? { ...i, value } : i)));
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const add = () => onChange([...items, newKeyedValue(idPrefix)]);

  const inputClasses =
    'flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';

  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        {icon}{label}
      </label>
      {items.length === 0 && emptyHint && (
        <p className="text-[11px] text-slate-400 mb-1.5">{emptyHint}</p>
      )}
      <div className="flex flex-col gap-1.5">
        {items.map(item => (
          <div key={item.id} className="flex items-start gap-2">
            {multiline ? (
              <textarea
                value={item.value}
                onChange={e => update(item.id, e.target.value)}
                placeholder={placeholder}
                rows={2}
                className={`${inputClasses} resize-none`}
              />
            ) : (
              <input
                value={item.value}
                onChange={e => update(item.id, e.target.value)}
                placeholder={placeholder}
                className={inputClasses}
              />
            )}
            <button
              type="button"
              onClick={() => remove(item.id)}
              aria-label={`Remove ${label.toLowerCase()} entry`}
              className="w-6 h-6 mt-1.5 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-semibold transition-colors w-fit"
        >
          <Plus size={12} />{addLabel}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Provenance
// ============================================================================

export interface SourceFieldProps {
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
}

/**
 * Where a requirement came from. Previously this was jammed into the story
 * text as a "[Source: ...]" bracket because there was nowhere else to put it.
 */
export function SourceField({ value, onChange, icon }: Readonly<SourceFieldProps>) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        {icon}Source
      </label>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g. FRP requirements spreadsheet HLR16, or workshop 2 transcript"
        className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
      />
    </div>
  );
}
